import { ApplicationElement, html } from '../application_element/element.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { colorFromString } from './lib/color_from_string.js';
import style from './style.lit.css';

import '../icon/element.js';

/**
 * Presents simple avatar placeholder, icon, initials or picture.
 *
 * Each avatar with name attribute has auto generated custom background color.
 *
 *
 * @prop name - Full name of an entity (user, group, community, etc...)
 * @prop picture - Picture url (optional)
 * @prop icon - Displayed when name and picture are empty (optional)
 * @prop layout - Predefined sizes (options: md, lg)
 * @prop disableInitials - Will not generate initials from name (optional)
 *
 * @slot default - For any additional elements.
 *
 * @cssprop [--avatar-size=50px] Use custom avatar size other than predefined via layout.
 *
 */
export default class Avatar extends ApplicationElement {
  static styles = [ApplicationElement.styles, style];

  static properties = {
    name: { type: String },
    picture: { type: String },
    icon: { type: String },
    label: { type: String },
    disableInitials: { type: Boolean },
    rounded: { type: Boolean, reflect: true },
    layout: { type: String, reflect: true },
  };

  constructor() {
    super();

    this.icon = 'user';
    this.layout = 'md';

    this.disableInitials = this.customConfig.disableInitials;
  }

  /** @internal */
  get bgColor() {
    return colorFromString(this.name, 50, 52);
  }

  /** @internal */
  get initials() {
    return this.name
      .split(/\s+/)
      .map(part => part[0])
      .slice(0, 2)
      .join('');
  }

  renderPicture() {
    return html` <img src="${this.picture}" alt=${ifDefined(this.name)} /> `;
  }

  renderPlaceholder() {
    return html` <ef-icon name="${this.icon}"></ef-icon> `;
  }

  renderInitials() {
    return html`
      <span class="name" style="background-color: ${this.bgColor}">
        ${this.initials}
      </span>
    `;
  }

  renderLabel() {
    return html` <span class="label">${this.label}</span> `;
  }

  renderContent() {
    if (this.picture) {
      return this.renderPicture();
    }

    if (!this.disableInitials && this.name) {
      return this.renderInitials();
    }

    return this.renderPlaceholder();
  }

  render() {
    return html`
      ${this.renderContent()} ${this.label && this.renderLabel()}
      <slot></slot>
    `;
  }
}

customElements.get('ef-avatar') || customElements.define('ef-avatar', Avatar);
